import React, { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import PercentChangeBadge from './PercentChangeBadge.tsx'

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels)

// Predefined colors for consistent chart rendering
const CHART_COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#F97316', // orange-500
  '#14B8A6', // teal-500
  '#6366F1', // indigo-500
  '#84CC16', // lime-500
  '#A855F7', // purple-500
]

interface DonutChartItem {
  source?: string
  stage?: string
  label?: string
  count: number
}

interface DonutChartCardProps {
  title: string
  dateRange?: string
  centerValue: number | string
  centerLabel?: string
  percentChange?: number
  items: DonutChartItem[]
  className?: string
}

export default function DonutChartCard({
  title,
  dateRange,
  centerValue,
  centerLabel,
  percentChange,
  items,
  className = '',
}: DonutChartCardProps) {
  const chartData = useMemo(() => {
    const labels = items.map(item => item.source || item.stage || item.label || 'Unknown')
    const values = items.map(item => item.count)
    const colors = items.map((_, i) => CHART_COLORS[i % CHART_COLORS.length])

    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
          hoverOffset: 4,
        },
      ],
    }
  }, [items])

  const total = useMemo(() => items.reduce((s, i) => s + i.count, 0), [items])

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '55%',
      layout: {
        padding: 20,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || ''
              const value = context.parsed || 0
              const pct = total > 0 ? Math.round((value / total) * 100) : 0
              return `${label}: ${value} (${pct}%)`
            },
          },
        },
        datalabels: {
          color: '#374151',
          font: { size: 10, weight: 'bold' as const },
          anchor: 'end' as const,
          align: 'end' as const,
          offset: 4,
          formatter: (value: number, context: any) => {
            const pct = total > 0 ? Math.round((value / total) * 100) : 0
            if (pct < 3) return '' // Hide labels for tiny slices
            const label = context.chart.data.labels?.[context.dataIndex] || ''
            // Truncate long labels
            const short = label.length > 12 ? label.substring(0, 10) + '…' : label
            return `${short}\n${pct}%`
          },
          textAlign: 'center' as const,
        },
      },
    }),
    [total],
  )

  const hasData = items.length > 0 && items.some(item => item.count > 0)

  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-6 shadow-card ${className}`}>
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {dateRange && <p className="text-xs text-gray-500 mt-0.5">{dateRange}</p>}
      </div>

      {/* Center value + % change */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-3xl font-bold text-gray-900">{centerValue}</p>
          {centerLabel && <p className="text-xs text-gray-500">{centerLabel}</p>}
        </div>
        {percentChange !== undefined && <PercentChangeBadge value={percentChange} />}
      </div>

      {/* Chart */}
      {hasData ? (
        <div className="relative" style={{ height: '240px' }}>
          <Doughnut data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm">
          No data available
        </div>
      )}

      {/* Legend */}
      {hasData && (
        <div className="mt-4 space-y-1.5 max-h-[120px] overflow-y-auto">
          {items.map((item, index) => {
            const label = item.source || item.stage || item.label || 'Unknown'
            const color = CHART_COLORS[index % CHART_COLORS.length]
            return (
              <div key={label} className="flex items-center justify-between text-xs">
                <div className="flex items-center min-w-0">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0 mr-2"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-gray-700 truncate">{label}</span>
                </div>
                <span className="text-gray-900 font-medium ml-2 flex-shrink-0">{item.count}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
